import { type ReactNode } from 'react'
import { RawButton } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { TableEmptyState } from '@/components/Table/TableEmptyState'
import { TableHeader, TableHost } from '@/components/Table'
import { match } from '@/utils/pattern-match-util'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import styles from './UserManagementTable.module.css'
import type { SelectionState } from '@/infra/selection-state'

export interface UserManagementTableColumn {
  id: string
  header: string
  sortable: boolean
  visible: boolean
}

export interface UserManagementTableHeaderRowProps {
  columns: UserManagementTableColumn[]
  selectionState: SelectionState
  onToggleAll: (checked: boolean) => void
}

export function UserManagementTableHeaderRow({
  columns,
  selectionState,
  onToggleAll,
}: UserManagementTableHeaderRowProps) {
  return (
    <>
      <TableHeader align="left">
        <Checkbox
          onChange={onToggleAll}
          checked={match<SelectionState, boolean | 'indeterminate'>(
            selectionState,
            {
              ['AllSelected']: () => true,
              ['NoneSelected']: () => false,
              ['SomeSelected']: () => 'indeterminate',
            },
          )}
        />
      </TableHeader>

      {columns
        .filter((col) => col.visible)
        .map((col) => (
          <TableHeader key={col.id} canSort={col.sortable} align="left">
            {col.header}
          </TableHeader>
        ))}

      <TableHeader />
    </>
  )
}

interface TableUserCountProps {
  count: number
  isAllWorkspaces: boolean
  isShowingInactiveUsers: boolean
  onInactiveChange: (value: boolean) => void
}

const formattedCount = (count: number) => {
  return Intl.NumberFormat('en-US').format(count)
}

function TableUserCount({
  count,
  isAllWorkspaces,
  isShowingInactiveUsers,
  onInactiveChange,
}: TableUserCountProps) {
  return isAllWorkspaces ? (
    <div className={styles.tableUserCount}>
      Users
      <span data-test="users-table-count" className={styles.resultCount}>
        ({formattedCount(count ?? 0)})
      </span>
    </div>
  ) : (
    <Dropdown
      autoDismiss
      position="bottom-start"
      button={
        <RawButton className={styles.userStatusDropdown}>
          {isShowingInactiveUsers ? 'Inactive users' : 'Active users'}
          <span data-test="users-table-count" className={styles.resultCount}>
            ({formattedCount(count ?? 0)})
          </span>
          <Icon auto name="dropdown" />
        </RawButton>
      }
      testId="user-management-active-status-dropdown"
    >
      <DropdownTitle>Users</DropdownTitle>
      <DropdownItem
        active={isShowingInactiveUsers === false}
        buttonTestId="dropdown-active-status-option"
        checkmark={isShowingInactiveUsers === false}
        onPress={() => onInactiveChange(false)}
      >
        Active
      </DropdownItem>
      <DropdownItem
        active={isShowingInactiveUsers === true}
        buttonTestId="dropdown-inactive-status-option"
        checkmark={isShowingInactiveUsers === true}
        onPress={() => onInactiveChange(true)}
      >
        Inactive
      </DropdownItem>
    </Dropdown>
  )
}

export interface UserManagementTableProps {
  totalUsers: number
  /**
   * Whether "All workspaces" is selected, which hides the active/inactive
   * switch.
   */
  isAllWorkspaces: boolean
  /**
   * The inactive filter's selected value; `true` shows inactive users.
   */
  isShowingInactiveUsers: boolean
  onInactiveChange: (value: boolean) => void
  controls: ReactNode
  showEmptyState: boolean
  isSearchFilterApplied: boolean
  corporateMode: boolean
  onClearSearchFilter: () => void
  table: ReactNode
}

export function UserManagementTable({
  totalUsers,
  isAllWorkspaces,
  isShowingInactiveUsers,
  onInactiveChange,
  controls,
  showEmptyState,
  isSearchFilterApplied,
  corporateMode,
  onClearSearchFilter,
  table,
}: UserManagementTableProps) {
  return (
    <div className={styles.userTable}>
      <TableUserCount
        count={totalUsers}
        isAllWorkspaces={isAllWorkspaces}
        isShowingInactiveUsers={isShowingInactiveUsers}
        onInactiveChange={onInactiveChange}
      />

      <TableHost>
        {controls}

        {!showEmptyState ? (
          table
        ) : (
          <TableEmptyState bordered>
            <NoResultsEmptyState
              corporateMode={corporateMode}
              isSearchFilterApplied={isSearchFilterApplied}
              onClearSearchFilter={onClearSearchFilter}
            />
          </TableEmptyState>
        )}
      </TableHost>
    </div>
  )
}
